import {useParams, useSearchParams} from 'react-router-dom'
import {
    filtersSearchParamsName,
    idSearchParamsName,
    isPublicSearchParamsName,
    itemTypeFilterSearchParamsName,
    labelSearchParamsName,
    managedByGuildIdUrlSearchParamsName,
    notDefinedByOrganizationsUrlParamsName,
    organizationFilterSearchParamsName,
    pageSizeURLSearchParamsName,
    pageURLSearchParamsName,
    partNumberSearchParamsName,
    phraseUrlSearchParamsName,
    visibleByGuildIdUrlSearchParamsName
} from '../SearchParams'
import {toPageSize} from '../../util/component/search'
import {useCurrentGuildContext} from '../../util/routing'
import {ApplyFilterDTO, ApplyFilterItemTypeDTO, ApplyFilterOrgDTO} from '../model/SearchFilter'
import {
    definitionIdUrlSearchParamsName,
    directionURLSearchParamsName,
    fieldURLSearchParamsName,
    SortingDirection,
    SortingField,
    toSortingDirection,
    toSortingField
} from '../model/Sorting'
import {useMemo} from 'react'
import {validate as uuidValidate} from 'uuid'

const sortingSearchParamNames = [fieldURLSearchParamsName, directionURLSearchParamsName, definitionIdUrlSearchParamsName]

const isSortingByDefinition = (newField: string | null) => newField && uuidValidate(newField)

export const useFullSearchParams = () => {
    const [searchParams, setSearchParamsInternal] = useSearchParams()

    // Url parameters
    const [currentGuildContext] = useCurrentGuildContext()
    const {itemListId, classificationId: technicalClassificationNodeId} = useParams()

    // Search parameters
    const page = +(searchParams.get(pageURLSearchParamsName) ?? 1)
    const pageSize = toPageSize(searchParams.get(pageSizeURLSearchParamsName))

    const setPage = (pageNumber: number) => {
        if (pageNumber === page || page < 0) return

        searchParams.set(pageURLSearchParamsName, `${pageNumber}`)
        searchParams.set(pageSizeURLSearchParamsName, `${pageSize}`)
        setSearchParamsInternal(searchParams)
    }

    const phrase = searchParams.get(phraseUrlSearchParamsName) ?? ''
    const partNumberPhrase = searchParams.get(partNumberSearchParamsName) ?? ''
    const labelPhrase = searchParams.get(labelSearchParamsName) ?? ''
    const idPhrase = searchParams.get(idSearchParamsName) ?? ''

    const urlFilters: string | null = searchParams.get(filtersSearchParamsName)
    const organizationUrlFilters: string | null = searchParams.get(organizationFilterSearchParamsName)
    const itemTypeUrlFilters: string | null = searchParams.get(itemTypeFilterSearchParamsName)
    const notDefinedByOrganizationsUrlFilters: string | null = searchParams.get(notDefinedByOrganizationsUrlParamsName)
    const isPublicUrlFilters: string | null = searchParams.get(isPublicSearchParamsName)

    const managedByGuildId = searchParams.get(managedByGuildIdUrlSearchParamsName) ?? undefined
    const visibleByGuildId = searchParams.get(visibleByGuildIdUrlSearchParamsName) ?? undefined

    const filters = useMemo(() => {
        if (!urlFilters) return undefined
        try {
            const parsedFilters: ApplyFilterDTO[] = JSON.parse(urlFilters)
            return parsedFilters
        } catch (e) {
            searchParams.delete(filtersSearchParamsName)
            return undefined
        }
    }, [urlFilters])

    const organizations = useMemo(() => {
        if (!organizationUrlFilters) return null
        try {
            const parsedFilters: ApplyFilterOrgDTO = JSON.parse(organizationUrlFilters)
            return parsedFilters
        } catch (e) {
            searchParams.delete(organizationFilterSearchParamsName)
            return null
        }
    }, [organizationUrlFilters])

    const itemTypes = useMemo(() => {
        if (!itemTypeUrlFilters) return null
        try {
            const parsedFilters: ApplyFilterItemTypeDTO = JSON.parse(itemTypeUrlFilters)
            return parsedFilters
        } catch (e) {
            searchParams.delete(itemTypeFilterSearchParamsName)
            return null
        }
    }, [itemTypeUrlFilters])

    const notDefinedByOrganizations = useMemo(() => {
        const urlFilters: string | null = searchParams.get(notDefinedByOrganizationsUrlParamsName)
        if (!urlFilters) return
        try {
            const parsedFilters: ApplyFilterOrgDTO = {organizationIds: JSON.parse(urlFilters)}
            return parsedFilters
        } catch (e) {
            searchParams.delete(notDefinedByOrganizationsUrlParamsName)
            return null
        }
    }, [notDefinedByOrganizationsUrlFilters])

    const isPublic = useMemo(() => {
        const urlFilters: string | null = searchParams.get(isPublicSearchParamsName)
        if (!urlFilters) return
        try {
            return JSON.parse(urlFilters)
        } catch (e) {
            searchParams.delete(isPublicSearchParamsName)
            return null
        }
    }, [isPublicUrlFilters])

    const sortingDirection: SortingDirection | null = toSortingDirection(searchParams?.get(directionURLSearchParamsName))
    const sortingField: SortingField = toSortingField(searchParams?.get(fieldURLSearchParamsName))
    const sortingDefinitionId = searchParams.get(definitionIdUrlSearchParamsName)

    // End of search parameters

    const resetValue = (paramsName: string): boolean => {
        if (!searchParams.has(paramsName)) return false
        searchParams.delete(paramsName)
        searchParams.set(pageURLSearchParamsName, '1')
        return true
    }

    const phraseTools: SearchParamTools = {
        setValue: (value: string) => {
            searchParams.set(phraseUrlSearchParamsName, value)
            searchParams.set(pageURLSearchParamsName, '1')
        },
        exists: () => searchParams.has(phraseUrlSearchParamsName),
        resetValue: () => resetValue(phraseUrlSearchParamsName)
    }
    const partNumberTools: SearchParamTools = {
        setValue: (value: string) => {
            searchParams.set(partNumberSearchParamsName, value)
            searchParams.set(pageURLSearchParamsName, '1')
        },
        exists: () => searchParams.has(partNumberSearchParamsName),
        resetValue: () => resetValue(partNumberSearchParamsName)
    }
    const labelTools: SearchParamTools = {
        setValue: (value: string) => {
            searchParams.set(labelSearchParamsName, value)
            searchParams.set(pageURLSearchParamsName, '1')
        },
        exists: () => searchParams.has(labelSearchParamsName),
        resetValue: () => resetValue(labelSearchParamsName)
    }
    const idTools: SearchParamTools = {
        setValue: (value: string) => {
            searchParams.set(idSearchParamsName, value)
            searchParams.set(pageURLSearchParamsName, '1')
        },
        exists: () => searchParams.has(idSearchParamsName),
        resetValue: () => resetValue(idSearchParamsName)
    }
    const organizationsTools: SearchParamTools = {
        setValue: (value: string) => {
            searchParams.set(organizationFilterSearchParamsName, value)
            searchParams.set(pageURLSearchParamsName, '1')
        },
        exists: () => searchParams.has(organizationFilterSearchParamsName),
        resetValue: () => resetValue(organizationFilterSearchParamsName)
    }
    const itemTypesTools: SearchParamTools = {
        setValue: (value: string) => {
            searchParams.set(itemTypeFilterSearchParamsName, value)
            searchParams.set(pageURLSearchParamsName, '1')
        },
        exists: () => searchParams.has(itemTypeFilterSearchParamsName),
        resetValue: () => resetValue(itemTypeFilterSearchParamsName)
    }

    const sortingTools: SortingTools = {
        setSorting: (sortingField: SortingField, sortingDirection: SortingDirection) => {
            searchParams.set(directionURLSearchParamsName, sortingDirection)
            searchParams.set(pageURLSearchParamsName, '1')

            if (sortingField && isSortingByDefinition(sortingField)) {
                searchParams.set(fieldURLSearchParamsName, SortingField.DEFINITION_VALUE)
                searchParams.set(definitionIdUrlSearchParamsName, sortingField)
            } else {
                searchParams.set(fieldURLSearchParamsName, sortingField)
                searchParams.delete(definitionIdUrlSearchParamsName)
            }
            setSearchParamsInternal(searchParams)
        },
        resetSorting: () => {
            sortingSearchParamNames.forEach((paramName) => searchParams.delete(paramName))
            setSearchParamsInternal(searchParams)
            return true
        }
    }

    return {
        page,
        pageSize,
        setPage,
        phrase,
        partNumberPhrase,
        labelPhrase,
        idPhrase,
        filters,
        itemTypes,
        organizations,
        notDefinedByOrganizations,
        technicalClassificationNodeId,
        isPublic,
        visibleByGuildId,
        managedByGuildId,
        guildId: currentGuildContext?.guildId,
        itemListId,
        sortingDirection,
        sortingField,
        sortingDefinitionId,
        applySearchParams: () => setSearchParamsInternal(searchParams),
        phraseTools: phraseTools,
        partNumberTools: partNumberTools,
        labelTools: labelTools,
        idTools: idTools,
        organizationsTools: organizationsTools,
        itemTypesTools: itemTypesTools,
        sortingTools
    }
}

export type FullSearchParams = ReturnType<typeof useFullSearchParams>

export interface SearchParamTools {
    setValue: (value: string) => void,
    exists: () => boolean,
    resetValue: () => boolean
}

export interface SortingTools {
    setSorting: (sortingField: SortingField, sortingDirection: SortingDirection, sortingValue?: string) => void,
    resetSorting: () => boolean
}